/**
 * Utility functions for handling timestamps and timezone conversion
 */

/**
 * Format a timestamp to the user's local timezone
 * @param timestamp - ISO string or Date object
 * @param format - Format string ('full', 'date', 'time', 'datetime')
 * @returns Formatted timestamp string
 */
export const formatTimestamp = (
  timestamp: string | Date | null | undefined,
  format: 'full' | 'date' | 'time' | 'datetime' = 'full'
): string => {
  if (!timestamp) return 'Not specified';
  
  try {
    const date = new Date(timestamp);
    
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    const options: Intl.DateTimeFormatOptions = {};
    
    switch (format) {
      case 'date':
        options.year = 'numeric';
        options.month = '2-digit';
        options.day = '2-digit';
        break;
      case 'time':
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.second = '2-digit';
        break;
      case 'datetime':
        options.year = 'numeric';
        options.month = '2-digit';
        options.day = '2-digit';
        options.hour = '2-digit';
        options.minute = '2-digit';
        break;
      case 'full':
      default:
        options.year = 'numeric';
        options.month = '2-digit';
        options.day = '2-digit';
        options.hour = '2-digit';
        options.minute = '2-digit';
        options.second = '2-digit';
        options.timeZoneName = 'short';
        break;
    }
    
    return date.toLocaleString(undefined, options);
  } catch (error) {
    console.error('Error formatting timestamp:', error);
    return 'Invalid date';
  }
};

/**
 * Get the user's current timezone offset
 * @returns Timezone offset string (e.g., '+08:00', '-05:00')
 */
export const getCurrentTimezoneOffset = (): string => {
  const offset = new Date().getTimezoneOffset();
  const hours = Math.abs(Math.floor(offset / 60));
  const minutes = Math.abs(offset % 60);
  const sign = offset <= 0 ? '+' : '-';
  
  return `${sign}${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}`;
};

/**
 * Format a timestamp to show both UTC and local time
 * @param timestamp - ISO string or Date object
 * @returns Formatted string showing both times
 */
export const formatTimestampWithTimezone = (
  timestamp: string | Date | null | undefined
): string => {
  if (!timestamp) return 'Not specified';
  
  try {
    const date = new Date(timestamp);
    
    if (isNaN(date.getTime())) {
      return 'Invalid date';
    }
    
    // Format in local timezone
    const localTime = date.toLocaleString(undefined, {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      timeZoneName: 'short'
    });
    
    // Get timezone offset
    // const offset = getCurrentTimezoneOffset();
    // return `${localTime} (${offset})`;
    return `${localTime}`;
  } catch (error) {
    console.error('Error formatting timestamp with timezone:', error);
    return 'Invalid date';
  }
};
